import { useState } from 'react';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { WhatsAppButton } from '../components/WhatsAppButton';
import { Mail, Phone, MapPin, MessageCircle, Send } from 'lucide-react';
import { WhatsAppIcon } from '../components/WhatsAppIcon';
import { settingsAPI } from '../lib/api';
import { useApiQuery } from '../hooks/useApiQuery';
import { toast } from 'sonner';

const ContactPage = () => {
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', message: '' });
  const [loading, setLoading] = useState(false);

  const { data: settingsRes } = useApiQuery(['settings'], () => settingsAPI.get());
  const fallback = { phone: '', email: '', address: 'Kuwait City, Kuwait', whatsapp_number: '1234567890' };
  const info = { ...fallback, ...(settingsRes?.data || {}) };

  const set = (key) => (e) => setFormData((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      toast.success("Message sent! We'll get back to you soon.");
      setFormData({ name: '', email: '', phone: '', message: '' });
      setLoading(false);
    }, 1000);
  };

  const contactCards = [
    { icon: Phone, label: 'Phone', value: info.phone || '+965 XXXXXXXX', href: `tel:${info.phone}` },
    { icon: Mail, label: 'Email', value: info.email || 'info@gulfstar.com', href: `mailto:${info.email}` },
    { icon: MapPin, label: 'Address', value: info.address, href: null },
  ];

  return (
    <div className="min-h-screen flex flex-col bg-[#f8fafc]">
      <Navbar />

      {/* Hero */}
      <section className="hero-gradient py-14 border-b border-slate-200" data-testid="contact-hero">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <p className="text-[#8acb95] text-sm font-semibold uppercase tracking-wider mb-2">Contact Us</p>
          <h1
            className="text-4xl md:text-5xl font-bold text-slate-800 mb-3"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Get in Touch
          </h1>
          <p className="text-slate-500 max-w-xl">
            Have questions or need help? Our team is here to assist you with products, orders, and bulk pricing.
          </p>
        </div>
      </section>

      {/* Main content */}
      <section className="py-14 flex-1" data-testid="contact-form-section">
        <div className="container mx-auto px-4 md:px-8 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">

            {/* Left — Contact info */}
            <div className="lg:col-span-2 space-y-5">
              <h2
                className="text-2xl font-bold text-slate-800 mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Contact Information
              </h2>

              {contactCards.map(({ icon: Icon, label, value, href }) => (
                <div key={label} className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-green-50 rounded-2xl flex items-center justify-center flex-shrink-0 border border-green-100">
                    <Icon className="w-5 h-5 text-[#8acb95]" />
                  </div>
                  <div>
                    <p className="text-xs text-slate-400 uppercase tracking-widest font-medium mb-0.5">{label}</p>
                    {href ? (
                      <a href={href} className="text-slate-700 font-semibold hover:text-[#8acb95] transition-colors text-sm">
                        {value}
                      </a>
                    ) : (
                      <p className="text-slate-700 font-semibold text-sm">{value}</p>
                    )}
                  </div>
                </div>
              ))}

              {/* WhatsApp CTA card */}
              <div className="mt-6 bg-gradient-to-br from-[#128C7E] to-[#25D366] rounded-2xl p-6 text-white">
                <div className="flex items-center gap-2 mb-2">
                  <WhatsAppIcon className="w-5 h-5" />
                  <h3 className="font-bold" style={{ fontFamily: 'Outfit, sans-serif' }}>Chat on WhatsApp</h3>
                </div>
                <p className="text-white/80 text-sm mb-4">
                  Get instant responses for product queries and bulk pricing.
                </p>
                <a
                  href={`https://wa.me/${info.whatsapp_number}?text=${encodeURIComponent('Hello, I would like to inquire about your products.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-whatsapp-button"
                  className="inline-flex items-center gap-2 bg-white text-[#128C7E] hover:bg-white/90 font-semibold px-5 py-2.5 rounded-xl text-sm transition-all shadow-sm"
                >
                  <WhatsAppIcon className="w-4 h-4" />
                  Start Chat
                </a>
              </div>
            </div>

            {/* Right — Form */}
            <div className="lg:col-span-3">
              <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-8">
                <h2
                  className="text-xl font-bold text-slate-800 mb-6"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  Send us a Message
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4" data-testid="contact-form">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Full Name *</label>
                      <input
                        required
                        type="text"
                        placeholder="Your name"
                        value={formData.name}
                        onChange={set('name')}
                        data-testid="contact-name-input"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-1.5">Phone</label>
                      <input
                        type="tel"
                        placeholder="+965 XXXXXXXX"
                        value={formData.phone}
                        onChange={set('phone')}
                        data-testid="contact-phone-input"
                        className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Email Address *</label>
                    <input
                      required
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={set('email')}
                      data-testid="contact-email-input"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-1.5">Message *</label>
                    <textarea
                      required
                      placeholder="How can we help you?"
                      rows={5}
                      value={formData.message}
                      onChange={set('message')}
                      data-testid="contact-message-input"
                      className="w-full px-4 py-3 border border-slate-200 rounded-xl text-sm focus:outline-none focus:border-[#8acb95] focus:ring-2 focus:ring-[#8acb95]/10 bg-white resize-none"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    data-testid="contact-submit-button"
                    className="w-full flex items-center justify-center gap-2.5 bg-[#8acb95] hover:bg-[#7bb885] disabled:bg-slate-300 text-white font-bold py-4 rounded-2xl transition-all shadow-sm hover:shadow-md text-sm"
                  >
                    {loading ? 'Sending...' : (
                      <>
                        <Send className="w-4 h-4" />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ContactPage;
